import { Link } from "react-router-dom";
import { ArrowRight, Flame } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { btnPrimary } from "@/lib/ui";

const VideoFrame = ({ base, label, caption }) => (
  <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-[#0c1319] shadow-[0_0_48px_rgba(43,159,215,0.12)]">
    <video
      autoPlay
      muted
      loop
      playsInline
      controls
      preload="metadata"
      className="aspect-video w-full object-cover"
      aria-label={label}
      data-testid={`video-${base}`}
    >
      <source src={`/videos/${base}-web.mp4`} type="video/mp4" />
      <source src={`/videos/${base}-web.webm`} type="video/webm" />
      Uw browser ondersteunt deze video niet.
    </video>
    <p className="border-t border-white/10 px-4 py-2.5 text-[10px] text-[#CBD3D9]/60">{caption}</p>
  </div>
);

const ADVANTAGES = [
  {
    base: "environmental-adaptability",
    title: "Ongeëvenaarde omgevingsaanpasbaarheid",
    label: "Video: thermische beeldvorming in duisternis, mist, regen en sneeuw",
    caption: "Beeldmateriaal: Hikvision — Unrivalled environmental adaptability.",
    text: "De beeldweergave van thermische camera's wordt niet beïnvloed door lichtomstandigheden — zelfs niet bij extreem weinig licht of volledige duisternis. Thermische beeldvorming presteert beter in zware weersomstandigheden zoals mist, smog, regen en sneeuw, waardoor het in vrijwel elke omgeving en op elk moment kan worden ingezet.",
  },
  {
    base: "perimeter-protection",
    title: "Grotere afstanden voor perimeterbeveiliging",
    label: "Video: geclassificeerde objectdetectie op grote afstand met thermische camera's",
    caption: "Beeldmateriaal: Hikvision — Extended distance on perimeter protection.",
    text: "Hikvision's thermische beeldvorming biedt een belangrijk voordeel voor perimeterbeveiliging doordat het geclassificeerde objectdetectie mogelijk maakt en personen, voertuigen, schepen en meer kan onderscheiden. Door regels zoals indringing of lijnoverschrijding in te stellen, kunnen deze camera's objecten snel en duidelijk markeren tegen hun achtergrond, zelfs op grote afstand.",
  },
];

export const FlamesVideo = () => (
  <section className="py-24 md:py-36" data-testid="flames-video-section">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
      <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
      <div>
        <Reveal>
          <div className="mb-5 flex items-center gap-4">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-[#FF8A00]/40 bg-[#FF8A00]/10 text-[#FF8A00]">
              <Flame size={20} strokeWidth={1.6} />
            </span>
            <span className="font-heading text-xs font-semibold uppercase tracking-[0.25em] text-[#62C3F0]">
              Warmtebeeldtechnologie
            </span>
          </div>
          <h2 className="font-heading text-base font-bold tracking-tight text-[#F5F7F9] sm:text-3xl md:text-4xl">
            Blijf de vlammen voor
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-6 text-base leading-relaxed text-[#CBD3D9]">
            De camera's van Hikvision met warmtebeeldtechnologie detecteren temperatuurafwijkingen
            voordat een brand begint, waardoor oververhitting van apparatuur of elektrische
            problemen vroegtijdig worden opgemerkt.
          </p>
          <p className="mt-4 text-base leading-relaxed text-[#CBD3D9]">
            Algoritmes zoals de zonnereflectie- en vorkheftruckfilters helpen valse alarmen te
            verminderen. Bij het beheer van bosbranden helpt warmtebeeldtechnologie om op het eerste
            gezicht een brandpunt te vinden en kan het koppelen aan Google Maps om brandcoördinaten
            te verzenden, waardoor waardevolle tijd wordt geboden voor brandpreventie en levens en
            eigendommen worden gered.
          </p>
        </Reveal>
        <Reveal delay={0.2}>
          <Link to="/offerte" className={`${btnPrimary} mt-8`} data-testid="flames-video-cta">
            Laat uw risicogebieden beoordelen <ArrowRight size={16} />
          </Link>
        </Reveal>
      </div>
      <Reveal delay={0.15}>
        <VideoFrame
          base="stay-ahead-of-the-flames"
          label="Video: Hikvision warmtebeeldtechnologie detecteert hitte voordat vlammen ontstaan"
          caption="Beeldmateriaal: Hikvision — Stay ahead of the flames."
        />
      </Reveal>
      </div>

      <Reveal className="mt-24 md:mt-32">
        <div className="mb-5 flex items-center gap-4">
          <span className="h-px w-10 bg-[#FF8A00]/50" aria-hidden="true" />
          <span className="font-heading text-xs font-semibold uppercase tracking-[0.25em] text-[#62C3F0]">
            Kerntechnologie
          </span>
        </div>
        <h3 className="max-w-3xl font-heading text-base font-bold tracking-tight text-[#F5F7F9] sm:text-3xl md:text-4xl">
          Belangrijkste voordelen van thermische beeldvorming
        </h3>
      </Reveal>

      <div className="mt-14 grid gap-20">
        {ADVANTAGES.map((adv, i) => (
          <div
            key={adv.base}
            className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16"
            data-testid={`advantage-${adv.base}`}
          >
            <Reveal className={i % 2 === 0 ? "lg:order-2" : ""}>
              <h4 className="font-heading text-xl font-bold tracking-tight text-[#F5F7F9] md:text-2xl">
                {adv.title}
              </h4>
              <p className="mt-5 text-base leading-relaxed text-[#CBD3D9]">{adv.text}</p>
            </Reveal>
            <Reveal delay={0.12} className={i % 2 === 0 ? "lg:order-1" : ""}>
              <VideoFrame base={adv.base} label={adv.label} caption={adv.caption} />
            </Reveal>
          </div>
        ))}
      </div>
    </div>
  </section>
);
